import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGoBack } from "@/lib/useGoBack";
import UserLink from "@/components/UserLink";
import { Calendar, MapPin, Globe, Mail, DollarSign, User as UserIcon, BookmarkPlus, BookmarkCheck, Users, Copy } from "lucide-react";
import { ContactIcon } from "@/components/ContactsField";
import CircuitFlag from "@/components/CircuitFlag";
import { toast } from "sonner";
import { formatDateRange, formatTimeRange, formatDayShort, todayLocalISO } from "@/lib/formatDate";
import { tzAbbrev } from "@/lib/timezone";
import { collapseRanges, GRADES, ALL_AGES, collapseYearRanges } from "@/lib/sports";
import MapLink from "@/components/MapLink";
import AddToCalendar from "@/components/AddToCalendar";
import { eventEndsAt } from "@/lib/calendar";
import ContactsDisplay from "@/components/ContactsDisplay";
import TipButton from "@/components/TipButton";
import ThankButton from "@/components/ThankButton";
import AuthorMenu from "@/components/AuthorMenu";
import TierPortal from "@/components/TierPortal";
import ShareButton from "@/components/ShareButton";
import { RsvpButtons } from "@/components/EventRsvp";
import FollowAlertsBell from "@/components/FollowAlertsBell";
import EditHistory from "@/components/EditHistory";
import WikiEditModal from "@/components/WikiEditModal";
import MessageBoard from "@/components/MessageBoard";
import Reviews from "@/components/Reviews";
import { ManageModeratorsModal } from "@/components/ClaimedListingsPanel";
import FlagButton from "@/components/FlagButton";
import ConfirmDialog from "@/components/ConfirmDialog";
import Seo from "@/components/Seo";
import { circuitSlug } from "@/lib/seo";
import { sportToSlug } from "@/lib/sportRoutes";
import DistanceZip from "@/components/DistanceZip";
import { savedNearZip, getSavedFilters, saveFilters } from "@/lib/filterCookies";
import useFitOneLine from "@/lib/useFitOneLine";
import { BadgeCheck, AlertTriangle } from "lucide-react";
import { Sparkles, X } from "lucide-react";

const _WN_LABELS = {
    event_date: "date", end_date: "end date", recurrence: "schedule", cost: "cost", type: "type",
    circuit: "circuit", level: "level", sport: "sport", city: "location", state: "location",
    zip: "location", street_address: "location", location_name: "location", description: "description",
    contacts: "contact info", contact_email: "contact info", website: "website",
    age_groups: "divisions", grades: "grades", genders: "gender", host_name: "host",
};

function _notListed(v) {
    const s = (v || "").trim();
    return s && s.toLowerCase() !== "not listed" ? s : "";
}

function _wnTimeAgo(iso) {
    try {
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    } catch { return ""; }
}

function ChangeHighlight({ event }) {
    const hist = event.edit_history || [];
    const last = hist.length ? hist[hist.length - 1] : null;
    const key = last ? `evt-whatsnew-${event.id}-${last.at}` : null;
    const [dismissed, setDismissed] = useState(() => {
        try { return key ? localStorage.getItem(key) === "1" : true; } catch { return false; }
    });
    if (!last || dismissed) return null;
    const labels = [];
    for (const f of (last.fields || [])) {
        const l = _WN_LABELS[f];
        if (l && !labels.includes(l)) labels.push(l);
    }
    if (labels.length === 0) return null;
    const dismiss = () => { try { if (key) localStorage.setItem(key, "1"); } catch { /* ignore */ } setDismissed(true); };
    return (
        <div className="max-w-5xl mx-auto px-6 md:px-8 relative z-10 mt-4" data-testid="event-whats-new">
            <div className="border border-[#22c55e]/40 bg-[#22c55e]/10 px-4 py-2.5 flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-[#22c55e] shrink-0" />
                <div className="flex-1 min-w-0 text-sm text-[#cfcfcf]">
                    <span className="text-[#22c55e] font-semibold uppercase tracking-wider text-[11px]">What's new</span>
                    <span className="ml-2">Updated {labels.join(", ")} · {_wnTimeAgo(last.at)}{last.username ? ` by ${last.username}` : ""}</span>
                </div>
                <button type="button" data-testid="event-whats-new-dismiss" onClick={dismiss} className="text-[#a0a0a0] hover:text-white shrink-0" aria-label="Dismiss">
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

export default function EventDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const autoEdit = searchParams.get("edit") === "1";
    const goBack = useGoBack();
    const { user, isFollowing, toggleFollow } = useAuth();
    const [event, setEvent] = useState(null);
    const [poster, setPoster] = useState(null);
    const [seriesDates, setSeriesDates] = useState([]);
    const nameFitRef = useFitOneLine(event?.name || "");
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const [showMods, setShowMods] = useState(false);
    const [showRelinquish, setShowRelinquish] = useState(false);
    const [zipState, setZipState] = useState(savedNearZip());
    const [coords, setCoords] = useState(null);  // precise device lat/lng, when granted
    // Setting a ZIP / location here recomputes distance AND persists the ZIP to the shared
    // filters so the main splash page autofills the same ZIP. Precise coords (if provided)
    // give a more accurate distance than the ZIP centroid.
    const handleZip = (z, c) => {
        setZipState(z);
        setCoords(c || null);
        saveFilters({ ...getSavedFilters({}), near_zip: z });
    };

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (coords) { params.near_lat = coords.lat; params.near_lng = coords.lng; if (zipState) params.near_zip = zipState; }
        else if (zipState) { params.near_zip = zipState; }
        api.get(`/events/${id}`, { params })
            .then((r) => setEvent(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id, zipState, coords]);

    // Canonicalize the URL to the SEO slug (old UUID links redirect to /events/<slug>).
    useEffect(() => {
        if (event && event.slug && id !== event.slug) navigate(`/events/${event.slug}`, { replace: true });
    }, [event, id, navigate]);

    useEffect(() => {
        // Show the owner (claimant) once verified, otherwise the original poster.
        const uid = event?.owner_verified ? (event.owner_user_id || event.created_by) : event?.created_by;
        if (uid) {
            api.get(`/users/${uid}/public`)
                .then((r) => setPoster(r.data))
                .catch(() => setPoster(null));
        }
    }, [event?.created_by, event?.owner_verified, event?.owner_user_id]);

    useEffect(() => {
        if (event?.recurrence === "custom" && event?.series_id) {
            api.get(`/events/${event.id}/dates`).then((r) => setSeriesDates(r.data || [])).catch(() => setSeriesDates([]));
        } else {
            setSeriesDates([]);
        }
    }, [event?.id, event?.recurrence, event?.series_id]);

    if (loading && !event) return <div className="text-center text-[#a0a0a0] py-20">Loading event…</div>;
    if (notFound || !event) {
        return (
            <div className="max-w-3xl mx-auto px-6 md:px-8 py-20 text-center">
                <div className="font-display text-4xl mb-3">Event not found</div>
                <button onClick={goBack} className="text-[#ff4500] uppercase tracking-[0.2em] text-xs">← Back</button>
            </div>
        );
    }

    const dateStr = formatDateRange(event.event_date, event.end_date);
    const timeStr = formatTimeRange(event.start_time, event.end_time);
    const tzAbb = tzAbbrev(event.timezone, event.event_date);
    const days = Array.isArray(event.day_times) ? event.day_times : [];
    const isMultiDay = days.length > 0;
    const todayISO = todayLocalISO();
    // An event is "over" the moment its end time passes (timezone-aware), which
    // drives both the "event has passed" note and when reviews open. If the end
    // time can't be determined (all-day / no time / no tz), fall back to the
    // date-only check so it still flips the day after.
    const eventEnd = eventEndsAt(event);
    const isPast = eventEnd
        ? Date.now() >= eventEnd.getTime()
        : (event.end_date || event.event_date || "") < todayISO;
    const cancelled = !!event.cancelled_at;
    const cancelDeleteDays = cancelled ? Math.max(0, Math.ceil((new Date(event.cancelled_at).getTime() + 7 * 24 * 3600 * 1000 - Date.now()) / (24 * 3600 * 1000))) : null;
    const isOwner = !!user && (user.is_admin || (event.owner_verified && event.owner_user_id === user.id));
    const canClaim = !!user && !event.owner_verified && event.owner_user_id !== user.id;

    const onClaim = async () => {
        if (!user) return toast.error("Please log in to claim this event");
        if (!user.is_pro) return toast.error("Only Pro members can claim listings — upgrade to Pro on your Account page");
        setClaiming(true);
        try {
            await api.post(`/claims/event/${event.id}`);
            const { data: fresh } = await api.get(`/events/${event.id}`);
            setEvent(fresh);
            toast.success("Claimed — your organizer name and contact matched this event");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not claim event");
        } finally { setClaiming(false); }
    };

    const onRelinquishEvent = async () => {
        try {
            await api.post(`/events/${event.id}/relinquish`);
            const { data: fresh } = await api.get(`/events/${event.id}`);
            setEvent(fresh);
            toast.success("You've relinquished ownership");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not relinquish");
        }
    };

    const renderEventContactInfo = (prefix) => (
        <div className="space-y-4">
            <div data-testid={`${prefix}-host`}>
                <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Organizer</div>
                <div className="text-sm text-white">{[event.host_name, event.organizer_name].map((s) => (s || "").trim()).filter(Boolean).join(" · ") || "Not Listed"}</div>
            </div>
            <ContactsDisplay title="Contact" contacts={event.contacts && event.contacts.length ? event.contacts : (event.contact_email ? [{ type: "email", value: event.contact_email }] : [])} accent="#22c55e" testidPrefix={`${prefix}-contact-disp`} />
            <InfoRow label="Cost" value={event.cost} icon={DollarSign} testid={`${prefix}-cost`} />
            {event.website && (
                <a href={event.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#22c55e] hover:underline text-sm" data-testid={`${prefix}-website`}>
                    <Globe className="h-4 w-4" /> Event information source
                </a>
            )}
        </div>
    );

    const renderEventActions = (prefix) => (
        <div className="space-y-4">
            {/* Share and Follow event compressed onto the same row */}
            <div className="flex items-stretch gap-3">
                <ShareButton
                    accent="#22c55e"
                    outline
                    className={`${isFollowing("event", event.id) ? "flex-[0.55]" : "flex-1"} h-11 text-xs`}
                    testid={`share-${prefix}-btn`}
                    shareUrl={`${process.env.REACT_APP_BACKEND_URL}/api/share/event/${event.id}`}
                    title={`${event.name} · ${event.sport} ${event.type}`}
                    description={`${event.name}\n${dateStr} · ${event.city}, ${event.state}`}
                />
                <div className={`${isFollowing("event", event.id) ? "flex-[1.45]" : "flex-1"} flex items-stretch min-w-0`}>
                    <button
                        type="button"
                        data-testid={`follow-${prefix}-btn`}
                        title={isFollowing("event", event.id) ? "Following" : "Follow"}
                        aria-label={isFollowing("event", event.id) ? "Following" : "Follow"}
                        disabled={followBusy}
                        onClick={async () => {
                            if (!user) return toast.error("Please log in to follow");
                            setFollowBusy(true);
                            const wasFollowing = isFollowing("event", event.id);
                            const res = await toggleFollow("event", event.id);
                            setFollowBusy(false);
                            if (res.ok) toast.success(wasFollowing ? "Unfollowed" : "You'll see this event's board posts in your feed and get notified of any updates to it");
                            else toast.error(res.error || "Follow failed");
                        }}
                        className={`${isFollowing("event", event.id) ? "flex-1 min-w-0 overflow-hidden" : "w-full"} h-11 inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider font-semibold whitespace-nowrap transition-colors ${isFollowing("event", event.id) ? "bg-[#22c55e] text-white hover:bg-[#16a34a]" : "bg-[#0f0f0f] border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10"}`}
                    >
                        {isFollowing("event", event.id) ? <BookmarkCheck className="h-3.5 w-3.5" /> : <BookmarkPlus className="h-3.5 w-3.5" />}
                        {isFollowing("event", event.id) ? "Following" : "Follow event"}
                    </button>
                    {isFollowing("event", event.id) && (
                        <FollowAlertsBell targetType="event" targetId={event.id} size={15} triggerStyle={{ color: "#fff" }} triggerClassName="inline-flex items-center justify-center h-11 w-11 shrink-0 bg-[#22c55e] border-l border-white/30 hover:bg-[#16a34a] transition-colors" />
                    )}
                </div>
            </div>

            {/* Add to calendar */}
            <AddToCalendar event={event} testid={`${prefix}-add-calendar`} />

            {/* RSVPs */}
            <RsvpButtons event={event} isOwner={isOwner} onConfigChange={(cfg) => setEvent((e) => ({ ...e, ...cfg }))} />

            {/* Posted by — label inline with avatar + username */}
            {poster && (
                <div className="border border-[#2a2a2a] bg-[#141414] p-4" data-testid={`${prefix}-posted-by`}>
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] shrink-0 flex items-center gap-1">{event.owner_verified ? <><BadgeCheck className="h-3.5 w-3.5 text-[#22c55e]" />Claimed by</> : "Posted by"}</span>
                        <div className="shrink-0">
                            <TierPortal count={poster.thanks_count} size={18} ring={1.5} gap={1} testid={`${prefix}-poster-portal`}>
                                {poster.photo ? (
                                    <img src={poster.photo} alt={poster.name} className="h-full w-full object-cover" />
                                ) : (
                                    <span className="h-full w-full flex items-center justify-center bg-[#0f0f0f]">
                                        <UserIcon className="h-3 w-3 text-[#a0a0a0]" />
                                    </span>
                                )}
                            </TierPortal>
                        </div>
                        <div data-testid={`${prefix}-poster-name`} className="text-sm text-white truncate min-w-0">{poster.username ? <UserLink username={poster.username} /> : poster.name}</div>
                        <ThankButton toUser={poster.id} toName={poster.username || poster.name} contextType="event" contextId={event.id} initialCount={poster.thanks_count} />
                        <TipButton tipLinks={poster.tip_links} name={poster.username || poster.name} />
                        <AuthorMenu userId={poster.id} name={poster.username || poster.name} reportType="event" reportId={event.id} />
                    </div>
                </div>
            )}
        </div>
    );


    return (
        <div className="min-h-screen" data-testid="event-detail-page">
            <Seo
                title={`${event.name}${event.sport ? ` — ${event.sport} Event` : ""}${event.city && event.state ? ` in ${event.city}, ${event.state}` : ""}`}
                description={`${event.name} is a ${event.sport || "sports"} event${event.city && event.state ? ` in ${event.city}, ${event.state}` : ""}${event.event_date ? ` on ${event.event_date}` : ""}. Find dates, details and registration on TravelSports.info.`}
                canonical={`/events/${event.slug || event.id}`}
                image={(event.photos && event.photos[0]) || undefined}
                type="article"
                jsonLd={{ "@context": "https://schema.org", "@type": "SportsEvent", name: event.name, ...(event.event_date ? { startDate: event.event_date } : {}), ...(event.end_date ? { endDate: event.end_date } : {}), ...(event.city && event.state ? { location: { "@type": "Place", name: event.location_name || `${event.city}, ${event.state}`, address: { "@type": "PostalAddress", addressLocality: event.city, addressRegion: event.state, addressCountry: "US" } } } : {}) }}
            />
            <ConfirmDialog
                open={showRelinquish}
                onOpenChange={setShowRelinquish}
                title="Relinquish ownership"
                description="Relinquish ownership of this event? It will become unclaimed and anyone can claim it again."
                confirmLabel="Relinquish"
                destructive
                onConfirm={onRelinquishEvent}
                testid="relinquish-confirm-dialog"
            />
            {/* Hero */}
            <div className="relative overflow-hidden border-b border-[#2a2a2a]">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0f1a12] via-[#0d0d0d] to-[#0a0a0a]" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-[#0a0a0a]" />
                <div className="relative max-w-5xl mx-auto px-6 md:px-8 flex flex-col pt-6 pb-6">
                    <h1 ref={nameFitRef} className="font-display tracking-wide leading-none whitespace-nowrap overflow-hidden" data-testid="event-title">
                        <span className={cancelled ? "text-red-500" : ""}>{event.name}</span>
                        {cancelled && <span className="text-red-500 font-sans text-lg md:text-2xl align-middle ml-3" data-testid="event-cancelled-title-label">(Cancelled?)</span>}
                    </h1>
                    {cancelled && (
                        <div className="mt-3 border border-red-600/60 bg-red-600/15 px-4 py-2.5 flex items-start gap-2 text-sm text-red-400" data-testid="event-cancelled-note-top">
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>This event was marked as <b>cancelled</b>. Please verify with the organization if you believe this is an error. It will be automatically removed in <b data-testid="event-cancelled-countdown">{cancelDeleteDays} day{cancelDeleteDays === 1 ? "" : "s"}</b>.</span>
                        </div>
                    )}
                    {!cancelled && isPast && (
                        <div className="mt-3 border border-red-600/60 bg-red-600/15 px-4 py-2.5 flex items-center gap-2 text-sm text-red-400" data-testid="event-passed-note-top">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            This event has passed. The message board stays open for follow-up discussion.
                        </div>
                    )}
                    <div className="text-sm text-[#cfcfcf] mt-2 flex flex-wrap gap-x-4 gap-y-1">
                        <AddToCalendar event={event} inline testid="event-date-cal" className={`inline-flex items-center gap-1.5 hover:text-[#22c55e] transition-colors ${isPast ? "text-red-400 font-medium" : ""}`}>
                            <Calendar className="h-3.5 w-3.5" /> {dateStr}{isMultiDay ? (tzAbb ? ` · ${tzAbb}` : "") : (timeStr ? ` · ${timeStr}${tzAbb ? " " + tzAbb : ""}` : "")}
                        </AddToCalendar>
                        <MapLink
                            direct
                            parts={[_notListed(event.street_address), event.city, event.state, event.zip]}
                            testid="event-location"
                            className="inline-flex items-center gap-1.5 hover:text-[#22c55e] transition-colors"
                        ><MapPin className="h-3.5 w-3.5" />
                            {_notListed(event.location_name) ? `${event.location_name} · ` : ""}
                            {_notListed(event.street_address) ? `${event.street_address}, ` : ""}
                            {event.city}, {event.state} {event.zip}
                        </MapLink>
                        <DistanceZip distanceMiles={event.distance_miles} zip={zipState} onZip={handleZip} accent="#22c55e" testid="event-distance" />
                    </div>
                </div>
            </div>

            <ChangeHighlight event={event} />

            <div className="max-w-5xl mx-auto px-6 md:px-8 pt-8 pb-2 md:pb-8 grid grid-cols-1 md:grid-cols-[1fr_288px] gap-x-8 gap-y-4 md:gap-8">
                <div className="space-y-4 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <EditHistory entity={event} entityType="event" entityId={event.id} onReverted={(fresh) => setEvent(fresh)} />
                        {(!isPast || user?.is_admin) && <WikiEditModal type="event" entity={event} autoOpen={autoEdit} onSaved={() => window.location.reload()} />}
                        {user && (
                            <Link
                                to={`/add/${encodeURIComponent(event.sport || "Basketball")}?${new URLSearchParams({ type: "event", duplicate: event.id, ...(event.claimed_entity_type && event.claimed_entity_id ? { claim_type: event.claimed_entity_type, claim_id: event.claimed_entity_id } : {}) }).toString()}`}
                                data-testid="duplicate-event-btn"
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#a0a0a0] hover:text-[#22c55e] transition-colors"
                                title="Create a new event with this event's details (minus the dates)"
                            >
                                <Copy className="h-3 w-3" /> Duplicate event
                            </Link>
                        )}
                        <FlagButton targetType="event" targetId={event.id} label="Report listing" />
                    </div>
                    {(event.age_groups?.length || event.grades?.length || event.grad_years?.length || event.genders?.length || event.circuits?.length) && (
                        <div className="space-y-2" data-testid="event-tags">
                            <div className="flex flex-wrap gap-2">
                                {(event.genders || []).map((g) => (
                                    <span key={`g-${g}`} className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-[#2a2a2a] bg-[#141414] text-[#cfcfcf]">
                                        {g === "M" ? "Boys" : g === "F" ? "Girls" : g}
                                    </span>
                                ))}
                                {collapseRanges((event.grades || []).filter((x) => x && x !== "N/A"), GRADES).map((gr) => (
                                    <span key={`gr-${gr}`} data-testid={`event-grade-${gr}`} className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#141414] ${(event.provided_dims || []).includes("grades") ? "border-2 border-white text-white font-bold" : "border border-[#2a2a2a] text-[#cfcfcf]"}`}>{gr}</span>
                                ))}
                                {collapseRanges((event.age_groups || []).filter((x) => x && x !== "N/A"), ALL_AGES).map((a) => (
                                    <span key={`a-${a}`} data-testid={`event-age-${a}`} className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#141414] ${(event.provided_dims || []).includes("age_groups") ? "border-2 border-white text-white font-bold" : "border border-[#2a2a2a] text-[#cfcfcf]"}`}>{a}</span>
                                ))}
                                {collapseYearRanges((event.grad_years || []).filter((x) => x && x !== "N/A")).map((y) => (
                                    <span key={`y-${y}`} data-testid={`event-grad-year-${y}`} className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#141414] ${(event.provided_dims || []).includes("grad_years") ? "border-2 border-white text-white font-bold" : "border border-[#2a2a2a] text-[#cfcfcf]"}`}>{y}</span>
                                ))}
                                {(event.circuits || []).filter((x) => x && x !== "N/A").map((c) => (
                                    <Link key={`c-${c}`} to={`/${sportToSlug(event.sport)}/${circuitSlug(c)}`} data-testid={`event-circuit-tag-${circuitSlug(c)}`} className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-[#ff4500]/50 bg-[#ff4500]/10 text-[#ff4500] hover:bg-[#ff4500]/20 inline-flex items-center transition-colors">
                                        {c}<CircuitFlag sport={event.sport} name={c} />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                    {isMultiDay && (
                        <div data-testid="event-daily-schedule">
                            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Daily Schedule{tzAbb ? ` · ${tzAbb}` : ""}</div>
                            <div className="border border-[#2a2a2a] bg-[#141414] divide-y divide-[#2a2a2a]">
                                {days.map((d, i) => {
                                    const tr = formatTimeRange(d.start, d.end);
                                    return (
                                        <div key={d.date} className="flex items-center justify-between px-3 py-2.5 text-sm" data-testid={`event-day-${i}`}>
                                            <span className="text-[#cfcfcf]"><span className="text-[#666] mr-2">Day {i + 1}</span>{formatDayShort(d.date)}</span>
                                            <span className={tr ? "text-white" : "text-[#666]"}>{tr ? `${tr}${tzAbb ? " " + tzAbb : ""}` : "Time TBD"}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {seriesDates.length > 1 && (
                        <div data-testid="event-series-schedule">
                            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Dates{tzAbb ? ` · ${tzAbb}` : ""}</div>
                            <div className="border border-[#2a2a2a] bg-[#141414] divide-y divide-[#2a2a2a]">
                                {seriesDates.map((d, i) => {
                                    const tr = formatTimeRange(d.start_time, d.end_time);
                                    const cancelled = !!d.cancelled_at;
                                    const isThis = d.id === event.id;
                                    return (
                                        <a
                                            key={d.id}
                                            href={`/events/${d.id}`}
                                            data-testid={`event-series-date-${i}`}
                                            className={`flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${isThis ? "bg-[#22c55e]/[0.06]" : "hover:bg-[#181818]"}`}
                                        >
                                            <span className={cancelled ? "text-[#888] line-through" : "text-[#cfcfcf]"}>
                                                <span className="text-[#666] mr-2">{i + 1}</span>{formatDayShort(d.event_date)}
                                                {cancelled && <span className="ml-2 text-[#ef4444] no-underline">(Cancelled)</span>}
                                            </span>
                                            <span className={tr ? "text-white" : "text-[#666]"}>{tr ? `${tr}${tzAbb ? " " + tzAbb : ""}` : "Time TBD"}</span>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {event.photo && (
                        <div data-testid="event-flyer">
                            <a href={event.photo} target="_blank" rel="noreferrer" className="block border border-[#2a2a2a] bg-[#141414] overflow-hidden hover:border-[#22c55e] transition-colors">
                                <img
                                    src={event.photo}
                                    alt={`${event.name} flyer`}
                                    className="w-full h-auto object-contain max-h-[600px] mx-auto"
                                    loading="lazy"
                                />
                            </a>
                        </div>
                    )}
                    {/* Mobile only: actions (share/follow/calendar/posted-by) between the flyer and About */}
                    <div className="md:hidden" data-testid="event-actions-mobile">
                        {renderEventActions("event-m")}
                    </div>
                    {event.description && (
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">About</div>
                            <p className="text-[#e5e5e5] whitespace-pre-line" data-testid="event-description">{event.description}</p>
                        </div>
                    )}
                    {/* Mobile only: organizer/contact/cost/source right after About */}
                    <div className="md:hidden pt-2" data-testid="event-contact-mobile">
                        {renderEventContactInfo("event-m")}
                    </div>
                </div>
                <div className="space-y-4">
                    {/* Desktop only: actions in the right column (mobile shows them above, between flyer and About) */}
                    <div className="hidden md:block">
                        {renderEventActions("event")}
                    </div>

                    {/* 6. Ownership — claim pending only; "Claimed by" now shows in the poster block */}
                    {!event.owner_verified && event.owner_user_id ? (
                        <div className="border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-amber-400" data-testid="event-claim-pending">
                            Ownership claim pending admin review
                        </div>
                    ) : null}
                    {isOwner && (
                        <button
                            type="button"
                            data-testid="add-moderators-btn"
                            onClick={() => setShowMods(true)}
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-[#0f0f0f] border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-black text-xs uppercase tracking-wider font-semibold transition-colors"
                        >
                            <Users className="h-4 w-4" /> Add moderators
                        </button>
                    )}
                    {event.owner_verified && (user?.is_admin || event.owner_user_id === user?.id) && (
                        <button
                            type="button"
                            data-testid="relinquish-btn"
                            onClick={() => setShowRelinquish(true)}
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-[#0f0f0f] border border-[#555] text-[#a0a0a0] hover:border-[#ef4444] hover:text-[#ef4444] text-xs uppercase tracking-wider font-semibold transition-colors"
                        >
                            Relinquish Ownership
                        </button>
                    )}
                    {canClaim && (
                        <button
                            type="button"
                            data-testid="claim-event-btn"
                            disabled={claiming}
                            onClick={onClaim}
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-[#0f0f0f] border border-white/30 text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider font-semibold transition-colors disabled:opacity-50"
                        >
                            <BadgeCheck className="h-4 w-4" /> {claiming ? "Claiming…" : "Claim ownership"}
                        </button>
                    )}

                    {/* Desktop only: organizer/contact/cost stay in the right column */}
                    <div className="hidden md:block">
                        {renderEventContactInfo("event")}
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 md:px-8 pb-4">
                {isPast && (
                    <div className="mb-4 border border-red-600/50 bg-red-600/10 px-4 py-3 flex items-center gap-2 text-sm text-red-400" data-testid="event-passed-note">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        This event has passed. The message board stays open for follow-up discussion.
                    </div>
                )}
                <MessageBoard entityType="event" entityId={event.id} accent="#22c55e" />
                {isPast && (
                    <Reviews entityType="event" entityId={event.id} canRespond={isOwner} accent="#22c55e" />
                )}
            </div>
            {showMods && <ManageModeratorsModal entityType="event" entityId={event.id} name={event.name} accent="#22c55e" onClose={() => setShowMods(false)} />}
        </div>
    );
}

function InfoRow({ label, value, icon: Icon, testid }) {
    if (!value) return null;
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-0.5">{label}</div>
            <div className="text-sm text-white inline-flex items-center gap-1.5">
                {Icon && <Icon className="h-3.5 w-3.5 text-[#22c55e]" />}
                {value}
            </div>
        </div>
    );
}

function contactHref(type, value) {
    const v = (value || "").trim();
    if (!v) return null;
    if (type === "email") return `mailto:${v}`;
    if (type === "phone") return `tel:${v.replace(/[^\d+]/g, "")}`;
    if (type === "instagram") return `https://instagram.com/${v.replace(/^@/, "")}`;
    if (type === "x")         return `https://x.com/${v.replace(/^@/, "")}`;
    if (type === "facebook") {
        // Accept full URLs, "facebook.com/..." or a handle
        if (/^https?:\/\//i.test(v)) return v;
        return v.includes("facebook.com/") ? `https://${v.replace(/^\/+/, "")}` : `https://facebook.com/${v.replace(/^@/, "")}`;
    }
    if (type === "website") return /^https?:\/\//i.test(v) ? v : `https://${v}`;
    return null;
}

const CONTACT_LABEL = {
    email: "Email",
    phone: "Phone",
    instagram: "Instagram",
    x: "X",
    facebook: "Facebook",
    website: "Website",
};

function ContactList({ contacts, legacyEmail }) {
    // Prefer the new multi-contact list; fall back to the legacy contact_email if empty.
    let rows = Array.isArray(contacts) ? contacts.filter((c) => c && c.value) : [];
    if (!rows.length && legacyEmail) rows = [{ type: "email", value: legacyEmail }];
    if (!rows.length) return null;
    return (
        <div data-testid="event-contacts">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-1">Contact</div>
            <div className="space-y-1.5">
                {rows.map((c, i) => {
                    const href = contactHref(c.type, c.value);
                    const label = CONTACT_LABEL[c.type] || "Contact";
                    return (
                        <div key={i} className="text-sm text-white" data-testid={`event-contact-${c.type}-${i}`}>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</div>
                            {href ? (
                                <a href={href} target={c.type === "email" || c.type === "phone" ? undefined : "_blank"} rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#22c55e] hover:underline break-all">
                                    <ContactIcon type={c.type} />
                                    <span>{c.value}</span>
                                </a>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 break-all">
                                    <ContactIcon type={c.type} /> {c.value}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

